import { cn } from "cn";
import type { FC } from "react";
import React from "react";

import { bindArgs } from "@core/foundation/function";
import type { ReadonlyRecord } from "@core/foundation/types";
import { hasValue } from "@core/foundation/types";

import { Badge } from "@react/ui-primitives/badge";
import { Button } from "@react/ui-primitives/button";

import type { Memory, Revision, Trigger } from "@apis/memory/models";

import { useMemoryActions } from "../hooks/useMemoryActions.ts";

const CONDITION_LABELS = {
  bashCommand: "Bash command",
  file: "File",
  toolCall: "Tool call",
} as const satisfies ReadonlyRecord<Trigger["condition"]["kind"], string>;

interface TriggerLineProps {
  readonly condition: Trigger["condition"];
}

const TriggerLine: FC<TriggerLineProps> = ({ condition }) => (
  <li className="flex flex-wrap items-center gap-2 text-xs">
    <Badge variant="secondary">{CONDITION_LABELS[condition.kind]}</Badge>
    {condition.kind === "file" ? (
      <>
        {hasValue(condition.glob) && <code>glob {condition.glob}</code>}
        {hasValue(condition.regexp) && <code>regexp {condition.regexp}</code>}
        {hasValue(condition.astGrep) && (
          <code>ast-grep {JSON.stringify(condition.astGrep)}</code>
        )}
        {condition.changeOnly && <span>on a change only</span>}
      </>
    ) : (
      <>
        <code>regexp {condition.regexp}</code>
        {condition.blocking && <span>blocking</span>}
      </>
    )}
  </li>
);

type StatusChange = NonNullable<Memory["lastStatusChange"]>;

function getArchivedText(change: StatusChange): string {
  switch (change.kind) {
    case "agent":
      return `Archived by an agent: ${change.reason}`;
    case "user":
      return "Archived by you";
  }
}

function getUnarchivedText(change: StatusChange): string {
  switch (change.kind) {
    case "agent":
      return `Unarchived by an agent: ${change.reason}`;
    case "user":
      return "Unarchived by you";
  }
}

interface StatusChangeLineProps {
  readonly change: StatusChange;
  readonly status: Memory["status"];
}

const StatusChangeLine: FC<StatusChangeLineProps> = ({ change, status }) => {
  switch (status) {
    case "active":
      return (
        <div className="text-xs text-muted-foreground">
          {getUnarchivedText(change)}
        </div>
      );
    case "archived":
      return (
        <div className="text-xs text-muted-foreground">
          {getArchivedText(change)}
        </div>
      );
  }
};

interface MemoryCardProps {
  readonly className?: string;
  readonly memory: Memory;
  readonly revision: Revision;
  readonly triggers: readonly Trigger[];
}

export const MemoryCard: FC<MemoryCardProps> = ({
  className,
  memory,
  revision,
  triggers,
}) => {
  const { archiveMemory, unarchiveMemory } = useMemoryActions();

  const isArchived = memory.status === "archived";

  const identifier = {
    memoryId: memory.id,
    projectId: memory.projectId,
  } as const;

  return (
    <div className={cn("flex flex-col gap-2 rounded-md border p-3", className)}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="font-medium">{memory.title}</span>
          {isArchived && <Badge variant="outline">Archived</Badge>}
        </div>
        {isArchived ? (
          <Button
            disabled={unarchiveMemory.isPending}
            onClick={bindArgs(unarchiveMemory.mutate, identifier)}
            variant="outline"
          >
            Unarchive
          </Button>
        ) : (
          <Button
            disabled={archiveMemory.isPending}
            onClick={bindArgs(archiveMemory.mutate, identifier)}
            variant="outline"
          >
            Archive
          </Button>
        )}
      </div>
      {hasValue(memory.lastStatusChange) && (
        <StatusChangeLine
          change={memory.lastStatusChange}
          status={memory.status}
        />
      )}
      <div className="text-sm whitespace-pre-wrap">{revision.content.text}</div>
      <ul className="flex flex-col gap-1">
        {triggers.map(trigger => (
          <TriggerLine condition={trigger.condition} key={trigger.id} />
        ))}
      </ul>
    </div>
  );
};
